import React, { useMemo } from 'react';
import {
	Box,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
} from '@barracuda-internal/bds-core-v2';
import Add from '@barracuda-internal/bds-core-v2/dist/Icons/Core/Add';
import Subtract from '@barracuda-internal/bds-core-v2/dist/Icons/Core/Subtract';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { Chip } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import type { CategoryGroup } from 'src/components/Filters/DIConditionCheckboxFilter';
import {
	DETECTION_FILTER_CONDITIONS,
	DETECTION_FILTER_POSSIBLE_VALUES,
	FILTER_TYPE_OPTIONS,
	ROW_TYPE_OPTIONS,
	SEARCH_FIELD_OPTIONS,
} from './constants';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface FilterRowProps {
	rowType: 'search' | 'filter' | ''; // Type column value
	scopeField: string; // Scope column value
	condition: string;
	values: string[];
	keyword: string; // For search type — free text
	onRowTypeChange: (rowType: string) => void;
	onScopeChange: (scope: string) => void;
	onConditionChange: (condition: string) => void;
	onValuesChange: (values: string[]) => void;
	onKeywordChange: (keyword: string) => void;
	onAdd: () => void;
	onRemove: () => void;
	disableAdd: boolean;
	showRemove: boolean;
	isSearchUsed: boolean; // Whether another row already uses "search"
	isFilterLimitReached: boolean; // Whether 3 filters are already used
	usedFilterScopes: string[]; // Filter scope values already used by other rows
	classifierCategories: CategoryGroup[];
}

type AutocompleteOption = { group: string; label: string; value: string };

const FilterRow: React.FC<FilterRowProps> = ({
	rowType,
	scopeField,
	condition,
	values,
	keyword,
	onRowTypeChange,
	onScopeChange,
	onConditionChange,
	onValuesChange,
	onKeywordChange,
	onAdd,
	onRemove,
	disableAdd,
	showRemove,
	isSearchUsed,
	isFilterLimitReached,
	usedFilterScopes,
	classifierCategories,
}) => {
	// Build type dropdown options — exclude "search" if already used by another row
	const availableTypeOptions = useMemo(
		() =>
			ROW_TYPE_OPTIONS.filter(
				(opt) =>
					opt.value === rowType ||
					(opt.value === 'search'
						? !isSearchUsed
						: opt.value === 'filter'
						? !isFilterLimitReached
						: true),
			),
		[rowType, isSearchUsed, isFilterLimitReached],
	);

	// Build scope dropdown options based on row type
	const scopeOptions = useMemo(() => {
		if (rowType === 'search') {
			return SEARCH_FIELD_OPTIONS;
		}
		if (rowType === 'filter') {
			// Exclude filter scopes already used by other rows (but keep current)
			return FILTER_TYPE_OPTIONS.filter(
				(opt) =>
					opt.value === scopeField ||
					!usedFilterScopes.includes(opt.value),
			);
		}
		return [];
	}, [rowType, scopeField, usedFilterScopes]);

	// Build autocomplete options for filter values
	const autocompleteOptions = useMemo((): AutocompleteOption[] => {
		if (rowType !== 'filter' || !scopeField) return [];
		if (scopeField === 'tags') {
			return classifierCategories.flatMap((cat) =>
				cat.options.map((opt) => ({
					group: cat.name,
					label: opt.label,
					value: opt.value,
				})),
			);
		}
		const config = DETECTION_FILTER_POSSIBLE_VALUES[scopeField];
		if (!config?.possibleValues) return [];
		return Object.entries(config.possibleValues).map(([key, val]) => ({
			group: '',
			label: val,
			value: key,
		}));
	}, [rowType, scopeField, classifierCategories]);

	// Map selected values to autocomplete option objects
	const selectedOptions = useMemo(
		() =>
			values
				.map((v) => autocompleteOptions.find((opt) => opt.value === v))
				.filter(Boolean) as AutocompleteOption[],
		[values, autocompleteOptions],
	);

	const isSearch = rowType === 'search';
	const isFilter = rowType === 'filter';
	const isScopeSelected = !!scopeField;

	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
			{/* Add / Remove buttons */}
			<Box sx={{ display: 'flex', width: 72, flexShrink: 0, gap: 0.5 }}>
				<IconButton
					bdsSize="medium"
					onClick={onAdd}
					disabled={disableAdd}
					aria-label="add row"
				>
					<Add fontSize="inherit" />
				</IconButton>
				{showRemove && (
					<IconButton
						bdsSize="medium"
						onClick={onRemove}
						aria-label="remove row"
					>
						<Subtract fontSize="inherit" />
					</IconButton>
				)}
			</Box>

			{/* Type dropdown — Search or Filter */}
			<FormControl size="small" sx={{ width: 130, flexShrink: 0 }}>
				<InputLabel>Type</InputLabel>
				<Select
					label="Type"
					value={rowType}
					size="small"
					onChange={(e) => {
						const newType = e.target.value as string;
						onRowTypeChange(newType);
						// Reset scope, values, keyword when type changes
						onScopeChange('');
						onValuesChange([]);
						onKeywordChange('');
						// Reset condition to Contains for search
						if (newType === 'search') {
							onConditionChange(
								DETECTION_FILTER_CONDITIONS[0].value,
							);
						}
					}}
				>
					{availableTypeOptions.map((opt) => (
						<MenuItem key={opt.value} value={opt.value}>
							{opt.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{/* Scope dropdown — search fields or filter fields based on type */}
			<FormControl
				size="small"
				sx={{ width: 160, flexShrink: 0 }}
				disabled={!rowType}
			>
				<InputLabel>Scope</InputLabel>
				<Select
					label="Scope"
					value={scopeField}
					size="small"
					onChange={(e) => {
						onScopeChange(e.target.value as string);
						onValuesChange([]);
						onKeywordChange('');
					}}
				>
					{scopeOptions.map((opt) => (
						<MenuItem key={opt.value} value={opt.value}>
							{opt.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{/* Condition dropdown — disabled for search, toggleable for filter */}
			<FormControl
				size="small"
				sx={{ width: 160, flexShrink: 0 }}
				disabled={isSearch || !isScopeSelected}
			>
				<InputLabel>Condition</InputLabel>
				<Select
					label="Condition"
					value={condition}
					size="small"
					onChange={(e) =>
						onConditionChange(e.target.value as string)
					}
				>
					{DETECTION_FILTER_CONDITIONS.map((c) => (
						<MenuItem key={c.value} value={c.value}>
							{c.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{/* Values column — TextField for search, Autocomplete for filter */}
			{isSearch ? (
				<TextField
					size="small"
					label="Keyword"
					value={keyword}
					onChange={(e) => onKeywordChange(e.target.value)}
					disabled={!isScopeSelected}
					sx={{ flex: 1 }}
				/>
			) : (
				<Autocomplete
					multiple
					size="small"
					sx={{ flex: 1 }}
					disabled={!isFilter || !isScopeSelected}
					options={autocompleteOptions}
					disableCloseOnSelect
					limitTags={2}
					getLimitTagsText={(more) => (
						<span
							style={{
								fontSize: '12px',
								color: 'gray',
								fontWeight: 500,
							}}
						>
							{`+${more} more`}
						</span>
					)}
					value={selectedOptions}
					groupBy={
						scopeField === 'tags'
							? (option) => option.group
							: undefined
					}
					getOptionLabel={(option) => option.label}
					isOptionEqualToValue={(option, val) =>
						option.value === val.value
					}
					onChange={(_, newValue) => {
						onValuesChange(newValue.map((opt) => opt.value));
					}}
					renderOption={(props, option) => (
						<li {...props} key={option.value}>
							<Checkbox
								icon={icon}
								checkedIcon={checkedIcon}
								style={{ marginRight: 8 }}
								checked={values.includes(option.value)}
								sx={{
									color: 'black',
									'&.Mui-checked': { color: 'black' },
								}}
							/>
							{option.label}
						</li>
					)}
					renderTags={(tagValues, getTagProps) =>
						tagValues.map((option, index) => (
							<Chip
								key={option.value}
								sx={{
									fontSize: '10px',
									height: '20px',
									fontWeight: 500,
								}}
								size="small"
								label={option.label}
								{...getTagProps({ index })}
							/>
						))
					}
					renderInput={(params) => (
						<TextField {...params} label="Values" />
					)}
				/>
			)}
		</Box>
	);
};

export default React.memo(FilterRow);
